<template>
  <div>
    <h3 v-if="showTitle" class="text-center text-base text-gray-dark font-bold flex justify-center block">
      <div :class="titleClasses">{{ title }}</div>
      <vs-tooltip class="flex ml-1 items-center" position="bottom">
        <template #content>
          <div v-html="ANSWER_DETAILS_TOOLTIP"/>
        </template>
        <vs-icon class="text-gray cursor-default" icon="info"/>
      </vs-tooltip>
    </h3>

    <sortable-table :id="id"
                    :key="isEmailBot"
                    ref="intentUsagesTable"
                    :data="tableData"
                    :headers="headers"
                    :use-first-col-order="true"
                    :is-loading="isLoading"
                    :variant="variant"
                    @click:sorter="handleSorter"
    >
      <template #td="{ row, tdClass, headers, cell, cellIndex }">
        <div class="flex">
          <component :is="isRouterLink(headers[cellIndex].columnId) ? 'router-link' : 'span'"
                     :class="isRouterLink(headers[cellIndex].columnId) ? 'text-body font-semibold' : 'text-gray-dark'"
                     :to="getRoute(headers[cellIndex].columnId, row)"
                     class="text-base leading-none align-text-bottom clamp hover:text-primary"
                     target="_blank"
          >
            {{ getCellText(cell) }}
          </component>

          <vs-tooltip v-if="getCellText(cell) === 'Unknown/Generated answer'"
                      class="flex ml-1 items-center"
                      position="bottom"
          >
            <template #content>
              <div v-html="UNKNOWN_ANSWER_TOOLTIP"/>
            </template>

            <vs-icon icon="info" class="text-gray cursor-default"/>
          </vs-tooltip>

          <span v-if="getCellSuffix(cell)"
                class="text-xs ml-1 mt-1 h-full text-gray-dark leading-none"
          >
              {{ getCellSuffix(cell) }}
          </span>
        </div>

      </template>
    </sortable-table>
  </div>
</template>

<script>
import SortableTable from '@/components/common/SortableTable'
import { ROUTE_NAMES } from '@/router'
import botModule from '@/store/modules/bot.module'
import { resolveAnswerRouteBotId } from '@/utils/market.utils'

const UNKNOWN_ANSWER_TOOLTIP = `If the request count for <strong>Usage Rate</strong> and <strong>Request Outcomes</strong> are not matching, it means:<br/>
  • some requests were NOT automated<br/>
  • "Unknown/Generated answer" did not use Chat GPT to respond to those requests
`

const ANSWER_DETAILS_TOOLTIP = `A comprehensive view of answer performance to provide insights, such as:</br>
  • which answers are used the most</br>
  • which answers need to be improved
`

export default {
  name: 'IntentsUsagesTable',

  components: { SortableTable },

  props: {
    isLoading: {
      type: Boolean,
      default: false
    },
    intents: {
      type: Array,
      default: () => []
    },
    totalRequests: {
      type: Number,
      default: 0
    },
    getSeriesData: {
      type: Function,
      default: () => {}
    },
    botId: {
      type: String,
      required: true
    },
    showTitle: {
      type: Boolean,
      default: true
    },
    title: {
      type: String,
      default: 'Answer Details'
    },
    titleClasses: {
      type: String,
      default: 'py-10'
    },
    id: {
      type: String,
      required: true
    },
    fullTitleBreakPoint: {
      type: String // tailwind breakpoints
    },
    variant: {
      type: String
    },
    routeQueryParams: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      tableData: [],
      ANSWER_DETAILS_TOOLTIP,
      UNKNOWN_ANSWER_TOOLTIP
    }
  },

  watch: {
    isLoading() {
      if (!this.isLoading) {
        this.initTableData()
      }
    },

    intents: {
      deep: true,
      immediate: false,
      handler() {
        this.initTableData()
        this.$refs.intentUsagesTable.initSorter()
      }
    }
  },

  computed: {
    isEmailBot() {
      return botModule.isEmailBot()
    },

    routeMarkets() {
      const { markets } = this.$route.query
      if (markets === '') return []
      return markets ? markets.split(',') : null
    },

    answerRouteBotId() {
      return resolveAnswerRouteBotId(this.botId, this.routeMarkets)
    },

    headers() {
      const headers = [
        {
          columnId: -1,
          text: '#',
          generalClasses: 'w-1/24 ignore-elements',
          titleClasses: 'truncate',
          hideSorters: true,
          hideDrag: true
        },
        {
          columnId: 0,
          text: 'Answer',
          generalClasses: `w-3/12 min-w-32 ${this.fullTitleBreakPoint}:min-w-64`,
          titleClasses: 'truncate'
        },
        {
          columnId: 1,
          text: 'Usage Rate',
          generalClasses: `${this.isEmailBot ? 'w-3/12' : 'w-2/12'} ${this.fullTitleBreakPoint}:min-w-48`,
          titleClasses: 'truncate',
          defaultSort: 'desc'
        },
        {
          columnId: 2,
          text: 'Fully Automated',
          key: 'fullyAutomated',
          generalClasses: `${this.isEmailBot ? 'w-3/12' : 'w-2/12'} min-w-24 ${this.fullTitleBreakPoint}:min-w-48`,
          titleClasses: 'truncate'
        },
        {
          columnId: 3,
          text: this.isEmailBot ? 'Requested Operator' : 'Partially Automated',
          key: this.isEmailBot ? 'requestedOperator' : 'partiallyAutomated',
          generalClasses: `${this.isEmailBot ? 'w-3/12' : 'w-2/12'} min-w-24 ${this.fullTitleBreakPoint}:min-w-48`,
          titleClasses: 'truncate'
        }
      ]

      if (!this.isEmailBot) {
        headers.push({
          columnId: 4,
          text: 'Wrong Answer',
          key: 'wrongAnswer',
          generalClasses: `w-2/12 min-w-32 ${this.fullTitleBreakPoint}:min-w-48`,
          titleClasses: 'truncate'
        })
        headers.push({
          columnId: 5,
          text: 'Requested Operator',
          key: 'requestedOperator',
          generalClasses: `w-2/12 min-w-32 ${this.fullTitleBreakPoint}:min-w-48`,
          titleClasses: 'truncate'
        })
      }

      return headers
    }
  },

  methods: {
    isRouterLink(columnId) {
      return columnId !== -1
    },

    getAnswerIdForRoute(intent) {
      if (!intent) return undefined
      const variant = intent.answer_ids?.find(({ botId }) => botId === this.answerRouteBotId)
      return variant?.id || intent.answer_id
    },

    getRoute(columnId, row) {
      const answerName = row[1]
      const intent = this.intents.find(({ answer_name }) => answer_name === answerName)
      const answer_id = this.getAnswerIdForRoute(intent)
      const links = [
        `/bots/${this.answerRouteBotId}/answers/${answer_id}`,
        this.getConversationsRoute(answer_id),
        this.getConversationsRoute(answer_id, 'fullyAutomated'),
        this.getConversationsRoute(answer_id, this.isEmailBot ? 'requestedOperator' : 'partiallyAutomated'),
        this.getConversationsRoute(answer_id, 'wrongAnswer'),
        this.getConversationsRoute(answer_id, 'requestedOperator')
      ]
      return links[columnId]
    },

    getConversationsRoute(answerId, requestOutcomes) {
      const { dateFrom, dateTo, channels, markets, bots } = this.$route.query
      const queryParams = { answerId, requestOutcomes, dateFrom, dateTo, ...this.routeQueryParams }
      if (typeof channels !== 'undefined') queryParams.channels = channels
      if (markets != null) queryParams.markets = markets
      if (typeof queryParams.bots === 'undefined' && typeof bots !== 'undefined') queryParams.bots = bots
      return { name: ROUTE_NAMES.history, query: queryParams }
    },

    getCellContent(lineIndex, totalUsages, outcomeType) {
      if (!this.totalRequests) return '0.00% (0)'

      const suffixTotal = (this.getSeriesData(outcomeType, true)[lineIndex] / 100 * this.totalRequests).toFixed(0)
      const percentage = (suffixTotal / totalUsages * 100 || 0).toFixed(2)
      return `${percentage}%(${suffixTotal})`
    },

    initTableData() {
      this.tableData = this.intents.map(({
        answer_name,
        usage
      }, i) => {
        return this.isEmailBot ? [
          answer_name,
          this.totalRequests ? `${(usage / this.totalRequests * 100).toFixed(2)}%(${usage})` : '0.00%(0)',
          this.getCellContent(i, usage, 'fullyAutomated'),
          this.getCellContent(i, usage, 'requestedOperator')
        ] : [
          answer_name,
          this.totalRequests ? `${(usage / this.totalRequests * 100).toFixed(2)}%(${usage})` : '0.00%(0)',
          this.getCellContent(i, usage, 'fullyAutomated'),
          this.getCellContent(i, usage, 'partiallyAutomated'),
          this.getCellContent(i, usage, 'wrongAnswer'),
          this.getCellContent(i, usage, 'requestedOperator')
        ]
      })
    },

    sortStringValues(order, columnId) {
      this.tableData = this.tableData.sort((a, b) => {
        if (order === 'asc') {
          return a[columnId].localeCompare(b[columnId])
        } else {
          return b[columnId].localeCompare(a[columnId])
        }
      })
    },

    sortNumberValues(order, columnId) {
      this.tableData = this.tableData.sort((a, b) => {
        if (order === 'asc') {
          return this.getNumericalValue(a[columnId]) - this.getNumericalValue(b[columnId])
        } else {
          return this.getNumericalValue(b[columnId]) - this.getNumericalValue(a[columnId])
        }
      })
    },

    handleSorter({ order, columnId }) {
      if (this.isValueNumerical(columnId)) {
        this.sortNumberValues(order, columnId)
      } else {
        this.sortStringValues(order, columnId)
      }
    },

    isValueNumerical(columnId) {
      const firstValueInColumn = this.tableData[0]?.[columnId]
      return typeof firstValueInColumn === 'number' || firstValueInColumn?.includes('%')
    },

    getNumericalValue(value) {
      if (typeof value === 'number') return value

      const percentIndex = value.indexOf('%')
      return percentIndex === -1 ? +value : +value.slice(0, percentIndex)
    },

    getCellText(text) {
      if (typeof text === 'number') return text

      const percentIndex = text.indexOf('%')
      return percentIndex === -1 ? text : text.slice(0, percentIndex + 1)
    },

    getCellSuffix(text) {
      if (typeof text !== 'number' && text.includes('%')) {
        const percentIndex = text.indexOf('%')
        return text.slice(percentIndex + 1, text.length)
      }
      return null
    }
  }
}
</script>
